/**
 * useUiArrival (B10) — hidden → arriving → shown, from the phase and the
 * intro's "ui" beat (§E.7). Published on the root as data-ui so every module
 * can key off one clock; the layout's own arrival animation runs from it (chrome.ts).
 */
import { useEffect, useRef, useState } from "react";
import type { Phase } from "@/contracts/store";
import { on } from "@/store/bus";
import { ARRIVAL_MS, ARRIVAL_REDUCED_MS, arrivalStagger, nextUiState, uiVisible, type UiState } from "./arrival";

export function useUiArrival(phase: Phase, reduced: boolean): { ui: UiState; stagger: boolean } {
  const [beatSeen, setBeatSeen] = useState(false);
  useEffect(
    () =>
      on("intro:beat", ({ beat }) => {
        if (beat === "ui") setBeatSeen(true);
      }),
    [],
  );
  // A fresh load (or a hard reset back to loading) waits for the beat again.
  useEffect(() => {
    if (phase === "loading") setBeatSeen(false);
  }, [phase]);

  const visible = uiVisible(phase, beatSeen);
  const [ui, setUi] = useState<UiState>(() => nextUiState("hidden", visible) === "arriving" ? "shown" : "hidden");
  const [stagger, setStagger] = useState(false);
  const uiRef = useRef(ui);
  uiRef.current = ui;

  useEffect(() => {
    const next = nextUiState(uiRef.current, visible);
    if (next === uiRef.current) return;
    if (next === "arriving") setStagger(arrivalStagger(phase === "intro", reduced));
    setUi(next);
    // phase/reduced are read at the moment of arrival only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (ui !== "arriving") return undefined;
    const t = window.setTimeout(() => setUi((u) => (u === "arriving" ? "shown" : u)), reduced ? ARRIVAL_REDUCED_MS + 40 : ARRIVAL_MS);
    return () => window.clearTimeout(t);
  }, [ui, reduced]);

  return { ui, stagger };
}
